import React from 'react'
import styled, { keyframes } from 'styled-components'
import { Tooltip } from '@mui/material'
import DescriptionIcon from '@mui/icons-material/Description'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import ArticleIcon from '@mui/icons-material/Article'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import VerifiedIcon from '@mui/icons-material/Verified'

const ReportSourceCitation = ({ source, onClick, bpNameById }) => {
  // Handle both object sources and string sources (legacy)
  const isStringSource = typeof source === 'string'
  const name = isStringSource ? source : (source?.name || source?.displayName || 'Unknown Document')
  const fileType = isStringSource ? null : source?.fileType
  const fileUrl = isStringSource ? null : source?.fileUrl
  const relevanceScore = isStringSource ? null : source?.relevanceScore
  const isEstateDocument = isStringSource ? false : !!source?.isEstateDocument
  const buildingPassportId = isStringSource ? null : source?.buildingPassportId
  const sourceLabel = bpNameById
    ? (isEstateDocument ? 'Estate document' : (buildingPassportId ? bpNameById[buildingPassportId] : null) || null)
    : null

  const getFileIcon = (fileName, type) => {
    const lowerName = (fileName || '').toLowerCase()
    const lowerType = (type || '').toLowerCase()

    if (lowerName.endsWith('.pdf') || lowerType.includes('pdf')) {
      return <PictureAsPdfIcon />
    }
    if (lowerName.match(/\.(jpg|jpeg|png|gif|webp)$/i) || lowerType.includes('image')) {
      return <ImageIcon />
    }
    if (lowerName.match(/\.(doc|docx)$/i) || lowerType.includes('word')) {
      return <ArticleIcon />
    }
    return <DescriptionIcon />
  }

  const getFileTypeLabel = (fileName, type) => {
    const lowerName = (fileName || '').toLowerCase()
    if (lowerName.endsWith('.pdf')) return 'PDF'
    if (lowerName.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return 'Image'
    if (lowerName.match(/\.(doc|docx)$/i)) return 'Word'
    if (lowerName.match(/\.(xls|xlsx)$/i)) return 'Excel'
    return 'Document'
  }

  const truncateName = (name, maxLength = 35) => {
    if (!name || name.length <= maxLength) return name
    const extension = name.split('.').pop()
    const baseName = name.slice(0, name.lastIndexOf('.'))
    const truncatedBase = baseName.slice(0, maxLength - extension.length - 4)
    return `${truncatedBase}...${extension}`
  }

  const getRelevanceColor = (score) => {
    if (score >= 0.8) return '#10B981'
    if (score >= 0.5) return '#F59E0B'
    return '#6B7280'
  }

  const tooltipContent = (
    <TooltipContainer>
      <TooltipHeader>
        <TooltipTitle>{name}</TooltipTitle>
        {fileUrl && <OpenInNewIcon fontSize="small" />}
      </TooltipHeader>
      <TooltipMeta>
        <TooltipMetaItem>
          <TooltipLabel>Type:</TooltipLabel>
          <TooltipValue>{getFileTypeLabel(name, fileType)}</TooltipValue>
        </TooltipMetaItem>
        {relevanceScore !== undefined && relevanceScore !== null && (
          <TooltipMetaItem>
            <TooltipLabel>Relevance:</TooltipLabel>
            <TooltipRelevance color={getRelevanceColor(relevanceScore)}>
              {Math.round(relevanceScore * 100)}%
            </TooltipRelevance>
          </TooltipMetaItem>
        )}
      </TooltipMeta>
      {fileUrl && (
        <TooltipAction>Click to view document</TooltipAction>
      )}
    </TooltipContainer>
  )

  return (
    <Tooltip title={tooltipContent} arrow placement="top" enterDelay={200}>
      <CitationChip
        onClick={() => onClick && onClick(source)}
        hasUrl={!!fileUrl}
        tabIndex={fileUrl ? 0 : -1}
        role={fileUrl ? 'button' : 'presentation'}
      >
        <IconContainer hasUrl={!!fileUrl}>
          {getFileIcon(name, fileType)}
        </IconContainer>
        <ContentWrapper>
          <FileNameRow>
            <FileName>{truncateName(name)}</FileName>
            {relevanceScore !== undefined && relevanceScore >= 0.8 && (
              <RelevanceBadge>
                <VerifiedIcon fontSize="inherit" />
              </RelevanceBadge>
            )}
          </FileNameRow>
          {sourceLabel && (
            <SourceSubLabel title={sourceLabel}>{sourceLabel}</SourceSubLabel>
          )}
        </ContentWrapper>
        {fileUrl && (
          <ViewIndicator>
            <OpenInNewIcon fontSize="inherit" />
          </ViewIndicator>
        )}
      </CitationChip>
    </Tooltip>
  )
}

export default ReportSourceCitation

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`

const CitationChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: linear-gradient(to right, #ffffff, #fafafa);
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: ${({ hasUrl }) => hasUrl ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  text-align: left;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }

  &:hover {
    ${({ hasUrl, theme }) => hasUrl && `
      background: linear-gradient(to right, ${theme.colors.primary}08, ${theme.colors.primary}04);
      border-color: ${theme.colors.primary}60;
      box-shadow: 0 4px 12px rgba(210, 71, 36, 0.12);
      transform: translateY(-1px);
    `}
  }

  &:active {
    ${({ hasUrl }) => hasUrl && `
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba(210, 71, 36, 0.08);
    `}
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 12px 14px;
  }
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme, hasUrl }) =>
    hasUrl ? `${theme.colors.primary}12` : '#F3F4F6'};
  border-radius: 8px;
  color: ${({ theme, hasUrl }) =>
    hasUrl ? theme.colors.primary : theme.colors.grey[200]};
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    font-size: 18px;
  }

  ${CitationChip}:hover & {
    ${({ hasUrl, theme }) => hasUrl && `
      background: ${theme.colors.primary}20;
      color: ${theme.colors.primary};
    `}
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const FileNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const SourceSubLabel = styled.span`
  font-size: 12px;
  color: #33becc;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;

  @media (max-width: 480px) {
    max-width: none;
  }
`

const FileName = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  line-height: 1.3;

  ${CitationChip}:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 480px) {
    max-width: none;
    flex: 1;
  }
`

const RelevanceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #10B981;
  font-size: 14px;
  flex-shrink: 0;
`

const ViewIndicator = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey[200]};
  font-size: 14px;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;

  ${CitationChip}:hover & {
    opacity: 1;
    transform: translateX(0);
    color: ${({ theme }) => theme.colors.primary};
  }
`

// Tooltip Styles
const TooltipContainer = styled.div`
  padding: 8px 4px;
  max-width: 280px;
`

const TooltipHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;

  svg {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`

const TooltipTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;
`

const TooltipMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
`

const TooltipMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TooltipLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`

const TooltipValue = styled.span`
  font-size: 12px;
  font-weight: 500;
`

const TooltipRelevance = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ color }) => color};
  background: rgba(255, 255, 255, 0.15);
  padding: 2px 8px;
  border-radius: 10px;
`

const TooltipAction = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`
